// 表格裁剪工具集（搬运自 umi/sub 项目，arco 适配版）。
// 提供：
//  - TableCropGuide / createEmptyTableCropGuide：浮层定位信息
//  - getCroppableTableRows：剔除已插入的分隔行
//  - getElementZoomScale：计算容器缩放比，定位时还原坐标
//  - getTableCropGuideAtPoint：鼠标坐标 → 命中表格行 + 浮层 top/left/width
//  - insertTableCropSplitRow：在命中行后插入或删除分隔行（视觉分段）

// 裁剪线左右额外延伸像素
const TABLE_CROP_LINE_EXTEND = 0;
// 分隔行占位高度
const TABLE_CROP_SPLIT_ROW_HEIGHT = 20;
export function createEmptyTableCropGuide() {
  return {
    visible: false,
    top: 0,
    left: 0,
    width: 0,
    rowIndex: -1,
    table: null
  };
}

// 仅保留真实数据行，过滤掉裁剪插入的 split 行。
export function getCroppableTableRows(table) {
  const body = table.tBodies?.[0];
  if (!body) return [];
  return Array.from(body.rows).filter(row => row.dataset.cropSplitRow !== 'true');
}

// 把一行所有 td 的 colspan 加起来，得到该行"逻辑列数"。
//  - 没设 colspan → 1
//  - colspan="3" → 3
// 用于让分隔行的 td.colSpan 真正横跨整行。
export function sumColSpan(row) {
  if (!row?.cells?.length) return 1;
  let total = 0;
  for (let i = 0; i < row.cells.length; i += 1) {
    const span = row.cells[i].colSpan;
    total += span && span > 0 ? span : 1;
  }
  return total || 1;
}

// 容器若有 transform / zoom，定位需还原成容器坐标。
export function getElementZoomScale(element) {
  if (!element) return 1;
  const transform = getComputedStyle(element).transform;
  if (!transform || transform === 'none') return 1;
  const match = transform.match(/^matrix\(([^,]+),/);
  if (match) {
    const value = parseFloat(match[1]);
    return Number.isFinite(value) && value !== 0 ? value : 1;
  }
  return 1;
}

/**
 * 鼠标坐标 → 表格裁剪线定位。
 * - 先用 elementsFromPoint 找到视口坐标命中的 table（必须在 container 内）
 * - 在表格各 row 中找到 clientY 命中的行
 * - 返回浮层 top/left/width（已除 zoom），给 TableCropOverlay 直接渲染
 */
export function getTableCropGuideAtPoint(container, clientX, clientY) {
  if (!container) return createEmptyTableCropGuide();
  const targets = typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(clientX, clientY) : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
  const table = targets.find(element => {
    const currentTable = element?.closest?.('table');
    return currentTable && container.contains(currentTable);
  })?.closest?.('table') || null;
  if (!table || !container.contains(table)) {
    return createEmptyTableCropGuide();
  }
  const rows = getCroppableTableRows(table);
  if (!rows.length) return createEmptyTableCropGuide();
  const containerRect = container.getBoundingClientRect();
  const zoomScale = getElementZoomScale(container);
  let rowIndex = -1;
  let lineTop = 0;
  for (let index = 0; index < rows.length; index += 1) {
    const rowRect = rows[index].getBoundingClientRect();
    if (clientY >= rowRect.top && clientY <= rowRect.bottom) {
      rowIndex = index;
      lineTop = (rowRect.bottom - containerRect.top) / zoomScale;
      break;
    }
  }
  if (rowIndex < 0) {
    let closestDistance = Number.POSITIVE_INFINITY;
    rows.forEach((row, index) => {
      const rowRect = row.getBoundingClientRect();
      const distance = Math.abs(clientY - rowRect.bottom);
      if (distance < closestDistance) {
        closestDistance = distance;
        rowIndex = index;
        lineTop = (rowRect.bottom - containerRect.top) / zoomScale;
      }
    });
  }
  const tableRect = table.getBoundingClientRect();
  return {
    visible: true,
    top: lineTop,
    left: (tableRect.left - containerRect.left - TABLE_CROP_LINE_EXTEND) / zoomScale,
    width: (tableRect.width + TABLE_CROP_LINE_EXTEND * 2) / zoomScale,
    rowIndex,
    table
  };
}

/**
 * 在表格指定行后插入"分隔行"，再次调用则移除。
 * - 命中行后若已有 split row → 删除
 * - 否则插入一个跨列空白行，作为视觉分段占位
 */
export function insertTableCropSplitRow(table, rowIndex) {
  if (!table || rowIndex < 0) return false;
  const body = table.tBodies?.[0];
  const rows = getCroppableTableRows(table);
  const targetRow = rows[rowIndex];
  if (!body || !targetRow) return false;
  const nextRow = targetRow.nextElementSibling;
  if (nextRow?.dataset?.cropSplitRow === 'true') {
    nextRow.remove();
    return true;
  }
  const splitRow = document.createElement('tr');
  splitRow.classList.add('crop-split-row');
  splitRow.dataset.cropSplitRow = 'true';
  splitRow.dataset.pageId = targetRow.getAttribute('id') || '';
  // 模板样式表对 tr/td 带有 !important 的高度规则，会把分隔行撑高，
  // 因此这里必须用 inline !important 显式锁定高度（同显隐行问题的解法）。
  splitRow.style.setProperty('height', `${TABLE_CROP_SPLIT_ROW_HEIGHT}px`, 'important');
  const cell = document.createElement('td');
  // 分隔行 colSpan = 命中行所有 td 的 colspan 之和。
  // 之前用 Math.max(cells.length, ...) 在存在 colspan="3" 的行会短一截。
  const cellCount = sumColSpan(targetRow);
  cell.colSpan = cellCount;
  cell.style.setProperty('padding', '0', 'important');
  cell.style.setProperty('border', 'none', 'important');
  cell.style.setProperty('background', 'transparent', 'important');
  cell.style.setProperty('height', `${TABLE_CROP_SPLIT_ROW_HEIGHT}px`, 'important');
  cell.style.setProperty('line-height', '0', 'important');
  cell.style.setProperty('font-size', '0', 'important');
  const spacer = document.createElement('div');
  spacer.classList.add('crop-split-spacer');
  spacer.style.setProperty('height', `${TABLE_CROP_SPLIT_ROW_HEIGHT}px`, 'important');
  spacer.style.setProperty('box-sizing', 'border-box', 'important');
  cell.appendChild(spacer);
  splitRow.appendChild(cell);
  body.insertBefore(splitRow, targetRow.nextSibling);
  return true;
}